import buildUrl from 'build-url';
import {useParams} from 'react-router';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import sortedIndexBy from 'lodash/sortedIndexBy';
import {TwitchPlayer, TwitchPlayerInstance} from 'react-twitch-embed';
import {Link} from 'react-router-dom';
import moment from 'moment';
import {UserInfo, usePulsoidClient} from '../../../PulsoidClient';
import {buildRoute} from '../../navigation/navigationUtils';
import {DASHBOARD_ROUTES} from '../../navigation/dashboard/DashboardRoutes';
import {
  Report,
  ReportClip,
  ReportStreamBpmLog,
} from '../../../types/Api/Reports';
import {ReportsV2ViewStatistics} from './ReportsV2ViewStatistics/ReportsV2ViewStatistics';
import css from './ReportsV2View.module.scss';
import {IconHeart} from '../../../system/icons/IconHeart';
import classNames from 'classnames';
import {ReportsV2ViewChart} from './ReportsV2ViewChart/ReportsV2ViewChart';
import {SkeletonLoader} from '../../../system/SkeletonLoader/SkeletonLoader';
import {ButtonV2} from '../../../system/ButtonV2/ButtonV2';
import {faXTwitter} from '@fortawesome/free-brands-svg-icons';
import {ReportsV2ViewClipModal} from './ReportsV2ViewClipModal/ReportsV2ViewClipModal';
import {IconLightning} from '../../../system/icons/IconLightning';
import {IconLink} from '../../../system/icons/IconLink';
import {PUBLIC_ROUTES} from '../../navigation/public/PublicRoutes';
import {isGuid} from '../../../system/utils';
import {ReportsV2ViewEmptyState} from './ReportsV2ViewEmptyState/ReportsV2ViewEmptyState';

const twitchUrl = buildUrl('https://pulsoid.net', {
  path: '/oauth/twitch/initiate',
  queryParams: {
    redirect: buildUrl('https://pulsoid.net', {
      path: '/ui/profile/settings',
    }),
  },
});

const formatClipDuration = (duration: string) => {
  const md = moment.duration(duration);
  return `${md.minutes()}:${md.seconds()}`;
};

type ReportsV2ViewProps = {isPublic?: boolean};

export const ReportsV2View = ({isPublic = false}: ReportsV2ViewProps) => {
  const pulsoidClient = usePulsoidClient({publicRpc: isPublic});
  const params = useParams<{id: string}>();
  const [report, setReport] = React.useState<Report>();
  const [profile, setProfile] = React.useState<UserInfo>();
  const [bpmLogs, setBpmLogs] = React.useState<ReportStreamBpmLog[]>([]);
  const [clipsSettings, setClipsSettings] = React.useState<any>();
  const twitchPlayerRef = useRef<TwitchPlayerInstance | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeClip, setActiveClip] = useState<ReportClip | null>(null);

  const xTwitterLink = useMemo(() => {
    return (
      'https://twitter.com/intent/tweet?text=' +
      encodeURIComponent('Check out my last stream Report by @pulsoid_app') +
      '&url=' +
      encodeURIComponent(
        buildRoute({
          path: PUBLIC_ROUTES.STREAM_REPORT,
          params: {id: report?.slug},
          full: true,
        })
      ) +
      '&hashtags=pulsoid'
    );
  }, [report?.slug]);

  const startTime = bpmLogs[0]?.createdAt
    ? new Date(bpmLogs[0]?.createdAt).getTime()
    : 0;

  const vodUrl = report?.vod?.vodUrl;
  const videoId = vodUrl?.split('/').pop();

  const reportsBackLink = buildRoute({
    path: DASHBOARD_ROUTES.REPORTS,
    query: document.location.search,
  });

  const fetchData = async (id: string) => {
    setIsLoading(true);

    // In order to support id and slug links
    const fetchReportReq = isGuid(id)
      ? pulsoidClient.getStreamReport(id)
      : pulsoidClient.getStreamReportBySlug(id);

    const {report} = await fetchReportReq;

    const [bpmLogsRes, profileRes, clipsSettingsRes] = await Promise.all([
      pulsoidClient.getStreamReportBpmLogs(report.id),
      ...(isPublic
        ? []
        : [
            pulsoidClient.getUserInfo(),
            pulsoidClient.getAutomatedClipsSettings(),
          ]),
    ]);

    setIsLoading(false);

    setReport(report);
    setBpmLogs(bpmLogsRes.logs);

    if (!isPublic) {
      setProfile(profileRes);
      setClipsSettings(clipsSettingsRes?.settings);
    }
  };

  useEffect(() => {
    fetchData(params.id);
  }, [params.id]);

  const updateVodByTimestamp = useCallback(
    (timestamp: number) => {
      const seconds = Math.abs(moment(report?.from).diff(timestamp, 'seconds'));

      twitchPlayerRef.current?.seek?.(seconds);
      twitchPlayerRef.current?.play();
    },
    [startTime]
  );

  const reportGames = report?.games.map((game) => game.name).join(' • ');
  const reportDuration = moment.duration(
    moment(report?.to).diff(moment(report?.from))
  );
  const reportsDurationDisplay = moment
    .utc(reportDuration.asMilliseconds())
    .format('H:mm:ss');
  const reportTimeAgo = moment(report?.to).fromNow();
  const reportSubtitle = `${reportGames} • ${reportsDurationDisplay} • ${reportTimeAgo}`;

  const clipsMaxBpm = useMemo(
    () =>
      report?.clips?.map((clip) => {
        const clipCreatedAt = new Date(clip.createdAt).getTime();
        const endBpmIndex = sortedIndexBy(
          bpmLogs,
          {createdAt: clipCreatedAt} as any,
          (log: ReportStreamBpmLog) => new Date(log.createdAt).getTime()
        );

        const clipDurationInMs = moment
          .duration(clip.duration)
          .asMilliseconds();
        const startBpmIndex = sortedIndexBy(
          bpmLogs,
          {createdAt: clipCreatedAt - clipDurationInMs} as any,
          (log: ReportStreamBpmLog) => new Date(log.createdAt).getTime()
        );

        const clipBpmLogs = bpmLogs.slice(startBpmIndex, endBpmIndex);
        const clipMaxBpm = Math.max(...clipBpmLogs.map((log) => log.bpm));

        return clipMaxBpm;
      }),
    [bpmLogs, report]
  );

  return (
    <div className={css.root}>
      <header className={css.header}>
        {!isPublic && (
          <div className={css.breadcrumbs}>
            <Link className={css.breadcrumbsItem} to={reportsBackLink}>
              Reports
            </Link>
          </div>
        )}

        <div className={css.headerContent}>
          <h1 className={css.headerTitle}>Stream report</h1>

          {!isPublic && !isLoading && (
            <div className={css.headerActions}>
              <ButtonV2
                type="secondary"
                prefixIcon={faXTwitter}
                href={xTwitterLink}
                target="_blank"
              >
                Post
              </ButtonV2>
              <ButtonV2
                type="secondary"
                prefixIconComponent={<IconLink />}
                copy={{
                  textToCopy: buildRoute({
                    path: PUBLIC_ROUTES.STREAM_REPORT,
                    params: {id: report?.slug},
                    full: true,
                  }),
                  notificationText: 'Public Link Copied!',
                }}
              >
                Copy Link
              </ButtonV2>
            </div>
          )}
        </div>
      </header>

      <hr className={css.divider} />

      <main className={css.content}>
        <section className={css.contentHeader}>
          {isLoading ? (
            <>
              <SkeletonLoader
                className={classNames(css.contentHeaderTitle, css.loading)}
                type="title"
                widthFromTo={[250, 400]}
              />
              <SkeletonLoader
                className={classNames(css.contentHeaderSubtitle, css.loading)}
                type="text"
                width={200}
              />
            </>
          ) : (
            <>
              <h3 className={css.contentHeaderTitle}>{report?.title}</h3>
              <div className={css.contentHeaderSubtitle}>{reportSubtitle}</div>
            </>
          )}
        </section>

        <section
          className={classNames(css.contentSection, css.statisticsWrapper)}
        >
          <ReportsV2ViewStatistics report={report} />
        </section>

        <section className={css.contentSection}>
          <div className={css.sectionHeader}>
            <div className={css.heartRateHeaderSection}>
              {isLoading ? (
                <SkeletonLoader
                  className={classNames(css.sectionTitle, css.loading)}
                  type="title"
                  widthFromTo={[100, 180]}
                />
              ) : (
                <h4 className={css.sectionTitle}>Heart rate dynamics</h4>
              )}

              <div className={css.reportHeartRateStats}>
                {isLoading ? (
                  <>
                    <SkeletonLoader type="text" widthFromTo={[50, 70]} />
                    <SkeletonLoader type="text" widthFromTo={[30, 40]} />
                  </>
                ) : (
                  <div className={css.reportHeartRateStatsText}>
                    <IconHeart
                      size={12}
                      className={css.reportHeartRateStatsIcon}
                    />
                    <span className={css.reportHeartRateStatsValue}>
                      {`${report?.statistics.avg}–${report?.statistics.max}`}
                    </span>
                    {' BPM,'}
                    <span className={css.reportSpikesStatsTextValue}>
                      {report?.statistics.spikes}
                    </span>
                    <span className={css.reportSpikesStatsText}>Spikes</span>
                  </div>
                )}
              </div>
            </div>

            <div className={css.heartRateHeaderSection}>
              <div className={css.legendPlaceholder}>
                <div className={css.lgFirst}>
                  <div className={css.legendPlaceholderIndicator} />
                  Spike BPM
                </div>
                <div className={css.lgSecond}>
                  <div className={css.legendPlaceholderIndicator} />
                  BPM
                </div>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className={css.chartLoaderWrapper}>
              <SkeletonLoader
                className={css.chartLoader}
                type="rect"
                width="100%"
              />
              <SkeletonLoader
                className={css.chartLoader}
                type="rect"
                width="100%"
              />
            </div>
          ) : (
            <ReportsV2ViewChart
              report={report}
              reportBpmLogs={bpmLogs}
              onPickTime={updateVodByTimestamp}
            />
          )}

          <div className={css.vodWrapper}>
            {isLoading ? (
              <SkeletonLoader className={css.chartLoaderVod} type="rect" />
            ) : videoId ? (
              <TwitchPlayer
                video={videoId}
                autoplay={false}
                muted={false}
                width={548}
                height={308}
                className={css.vodIframe}
                onReady={(player) => (twitchPlayerRef.current = player)}
              />
            ) : (
              <div className={css.vodEmptyState}>
                VOD is not available for this report
              </div>
            )}
          </div>
        </section>

        {!isPublic || report?.clips?.length ? (
          <>
            <section className={css.contentSection}>
              <div className={css.sectionHeader}>
                <div>
                  <h4
                    className={classNames(css.sectionTitle, {
                      [css.noClips]: !report?.clips?.length,
                    })}
                  >
                    Automated clips{' '}
                    {report?.clips?.length ? (
                      <span className={css.clipsTitleQty}>
                        {report?.clips?.length}
                      </span>
                    ) : null}
                  </h4>
                </div>
              </div>

              {report?.clips?.length ? (
                <div className={css.clipsGrid}>
                  {report?.clips.map((clip, index) => (
                    <div className={css.clipContainer} key={clip.id}>
                      <div className={css.clipTopBar}>
                        <div className={css.clipTopBarItem}>
                          {formatClipDuration(clip?.duration)}
                        </div>

                        <div className={css.clipTopBarItem}>
                          <IconLightning
                            className={css.clipTopBarLightningIcon}
                            size={12}
                          />
                          {`${clipsMaxBpm?.[index]} BPM`}
                        </div>
                      </div>

                      <img
                        onClick={() => setActiveClip(clip)}
                        src={clip.thumbnailUrl}
                        alt={clip.title}
                        className={css.clipImg}
                        key={clip.id}
                      />
                    </div>
                  ))}
                </div>
              ) : null}

              {!isPublic && (
                <ReportsV2ViewEmptyState
                  twitchUrl={twitchUrl}
                  clipsSettings={clipsSettings}
                  profile={profile}
                  report={report}
                />
              )}
            </section>
          </>
        ) : null}
      </main>

      <ReportsV2ViewClipModal
        clip={activeClip}
        onClose={() => setActiveClip(null)}
      />
    </div>
  );
};
